type ResponseHeaders = Record<string, unknown> & {
  get?: (name: string) => unknown;
};

type HttpError = {
  code?: string;
  message?: string;
  name?: string;
  response?: {
    status?: number;
    headers?: ResponseHeaders;
  };
};

type RetryOptions = {
  delays?: readonly number[];
  random?: () => number;
  sleep?: (milliseconds: number) => Promise<void>;
};

const retryableGatewayStatuses = new Set([502, 503, 504, 522]);
const retryableNetworkCodes = new Set([
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_NETWORK",
]);
const defaultRetryDelays = [300, 1_000] as const;

const getResponseHeader = (error: unknown, name: string): unknown => {
  const headers = (error as HttpError | null)?.response?.headers;

  if (!headers) {
    return undefined;
  }

  const getHeader = headers.get;

  if (typeof getHeader === "function") {
    return getHeader.call(headers, name);
  }

  const lowercaseName = name.toLowerCase();
  const matchingHeader = Object.keys(headers).find(
    (headerName) => headerName.toLowerCase() === lowercaseName,
  );

  return matchingHeader ? headers[matchingHeader] : undefined;
};

const getResponseStatus = (error: unknown): number | undefined =>
  (error as HttpError | null)?.response?.status;

export const isPlannedMaintenanceError = (error: unknown): boolean => {
  const retryAfter = getResponseHeader(error, "retry-after");

  return (
    getResponseStatus(error) === 503 &&
    retryAfter !== undefined &&
    retryAfter !== null &&
    String(retryAfter).trim() !== ""
  );
};

export const isTransientNetworkError = (error: unknown): boolean => {
  if (isPlannedMaintenanceError(error)) {
    return false;
  }

  const status = getResponseStatus(error);

  if (status !== undefined && retryableGatewayStatuses.has(status)) {
    return true;
  }

  const errorDetails = error as HttpError | null;

  if (errorDetails?.code && retryableNetworkCodes.has(errorDetails.code)) {
    return true;
  }

  const message = errorDetails?.message?.trim() ?? "";

  if (/^timeout of \d+ms exceeded(?:\b|\s|$)/i.test(message)) {
    return true;
  }

  return (
    errorDetails?.name === "TypeError" &&
    /^(failed to fetch|load failed|networkerror when attempting to fetch resource\.?|fetch failed)$/i.test(
      message,
    )
  );
};

const shouldRetryIdempotentRequest = (error: unknown): boolean => {
  return isTransientNetworkError(error);
};

const wait = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

export async function retryIdempotentRequest<T>(
  request: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const delays = options.delays ?? defaultRetryDelays;
  const random = options.random ?? Math.random;
  const sleep = options.sleep ?? wait;

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      const retryDelay = delays[attempt];

      if (retryDelay === undefined || !shouldRetryIdempotentRequest(error)) {
        throw error;
      }

      const jitteredDelay = retryDelay * (0.75 + random() * 0.5);
      await sleep(Math.round(jitteredDelay));
    }
  }
}
